import { retry, getData, postData } from 'utils/fetchUtilities';
import type { AudienceRequestData, AudienceResponse, CreateAudienceResponse, SchemaResponse, SegmentData, SegmentListResponse, SegmentResponse } from 'Listrak.Customer.Segmentation.Api.Client';
import { Channel } from 'Listrak.Customer.Segmentation.Api.V2.Client';
import type { QueryCountsResponseData } from 'Listrak.Customer.Segmentation.Api.V2.Client';
import type { CampaignMessage, MessageListResponse } from 'Listrak.Cdp.Campaign.Message.Api.Client';

const retryLimit = 3;

export const GetSegmentationSchema = async (url: string, token: string): Promise<SchemaResponse|Error> => {
  try {
    const response = await retry(
      () => getData(url, token),
      retryLimit,
    );

    return response;
  } catch (e) {
    return new Error(e);
  }
};

export const GetSavedSegment = async (url: string, segmentUid: string, token: string): Promise<SegmentResponse|Error> => {
  try {
    const _url = `${url}/${segmentUid}`;
    const response = await retry(
      () => getData(_url, token),
      retryLimit,
    );
    return response;
  } catch (e) {
    return new Error(e);
  }
};

export const SaveSegment = async (url: string, body: SegmentData, token: string): Promise<SegmentResponse|Error> => {
  try {
    const response = await retry(
      () => postData(url, body, token),
      retryLimit,
    );

    if (!response.ok) {
      return new Error(response.status === 409 ? 'Duplicate Name' : 'Network Error');
    }

    const data = await response.json();

    return data;
  } catch (e) {
    return new Error(e);
  }
};

export const GetAllSegments = async (url: string, excludeTrends: boolean, token: string): Promise<SegmentListResponse|Error> => {
  try {
    const queryParams = new URLSearchParams({excludeTrends: excludeTrends.toString()});
    const response = await retry(
      () => getData(url, token, null, queryParams),
      retryLimit,
    );

    return response;
  } catch (e) {
    return new Error(e);
  }
};

export const SaveAudience = async (url: string, body: AudienceRequestData, token: string): Promise<CreateAudienceResponse|Error> => {
  try {
    const response = await retry(
      () => postData(url, body, token),
      retryLimit,
    );
    const data = await response.json();

    return data;
  } catch (e) {
    return new Error(e);
  }
};

export const UpdateAudience = async (url: string, audienceUid: string, body: AudienceRequestData, token: string): Promise<void|Error> => {
  try {
    const _url = `${url}/${audienceUid}`;
    const response = await retry(
      () => postData(_url, body, token),
      retryLimit,
    );
    return response;
  } catch (e) {
    return new Error(e);
  }
};

export const GetSavedAudience = async (url: string, audienceUid: string, token: string): Promise<AudienceResponse|Error> => {
  try {
    const _url = `${url}/${audienceUid}`;
    const response = await retry(
      () => getData(_url, token),
      retryLimit,
    );

    return response;
  } catch (e) {
    return new Error(e);
  }
};

export const AutoSaveAudience = async (url: string, body: AudienceRequestData, token: string, signal: AbortSignal): Promise<CreateAudienceResponse|Error> => {
  try {
    const response = await postData(url, body, token, signal);
    const data = await response.json();

    return data;
  } catch (e) {
    return new Error(e);
  }
};

export const AutoUpdateAudience = async (url: string, audienceUid: string, body: AudienceRequestData, token: string, signal: AbortSignal): Promise<void|Error> => {
  try {
    const _url = `${url}/${audienceUid}`;
    const response = await postData(_url, body, token, signal) as unknown;
    return response as void;
  } catch (e) {
    return new Error(e);
  }
};

export const PostAudienceQuery = async (url: string, audienceUid: string | null, channel: Channel, subscriptionId: string, token: string, signal: AbortSignal): Promise<string|Error> => {
  const body = audienceUid !== null ? { audienceUid, channel, subscriptionId } : { channel, subscriptionId };
  try {
    const response = await postData(url, body, token, signal);
    return response.json();
  } catch (error: any) {
    return error;
  }
};

export const GetQueryCount = async (url: string, queryUid: string | null, token: string, signal: AbortSignal): Promise<QueryCountsResponseData|Error> => {
  try {
    const response = await postData(url, { queryUid: queryUid }, token, signal);
    return response.json();
  } catch (e) {
    return new Error(e);
  }
};

export const GetMessages = async (url: string, searchTerm: string, token: string): Promise<MessageListResponse | Error> => {
  try {
    const _url = `${url}${searchTerm}`;
    const response = await getData(_url, token);

    return response;
  } catch (e) {
    return new Error(e);
  }
}

export const GetMessageByUid = async (url: string, channels: Array<string>, uid: string, token: string): Promise<CampaignMessage | Error> => {
  try {
    const _url = `${url}?channels=${channels.join('|')}&MessageUid=${uid}`;
    const response = await getData(_url, token) as CampaignMessage;

    if (response.messagePid) {
      return response;
    }

  } catch (e) {
    return null;
  }
}
